import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, getToken, clearToken, errMsg } from "@/lib/api";
import { IMAGES } from "@/data/assets";
import { AdminLogin } from "@/components/admin/AdminLogin";
import { AdminEnquiries } from "@/components/admin/AdminEnquiries";
import { AdminOrders } from "@/components/admin/AdminOrders";
import { AdminGallery } from "@/components/admin/AdminGallery";
import { Loader2, LogOut, Inbox, Images, Package, Smartphone } from "lucide-react";

export default function Admin() {
  const [authed, setAuthed] = useState(null); // null = checking
  const [tab, setTab] = useState("enquiries");
  const [orderPrefill, setOrderPrefill] = useState(null);
  const [testingSms, setTestingSms] = useState(false);

  const convertToOrder = (enquiry) => {
    setOrderPrefill(enquiry);
    setTab("orders");
  };

  useEffect(() => {
    if (!getToken()) {
      setAuthed(false);
      return;
    }
    api
      .me()
      .then(() => setAuthed(true))
      .catch(() => {
        clearToken();
        setAuthed(false);
      });
  }, []);

  if (authed === null)
    return (
      <div className="min-h-[100svh] grid place-items-center text-white/40">
        <Loader2 className="animate-spin" />
      </div>
    );

  if (!authed) return <AdminLogin onSuccess={() => setAuthed(true)} />;

  const logout = () => {
    clearToken();
    setAuthed(false);
  };

  const sendTestSms = async () => {
    setTestingSms(true);
    try {
      const { data } = await api.sendTestOtp();
      toast.success(`Test code sent to ${data.phone_hint || "your phone"}.`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setTestingSms(false);
    }
  };

  const TABS = [
    { id: "enquiries", label: "Enquiries", icon: Inbox },
    { id: "orders", label: "Orders", icon: Package },
    { id: "gallery", label: "Gallery", icon: Images },
  ];

  const TITLES = {
    enquiries: "Enquiries",
    orders: "Orders",
    gallery: "Gallery",
  };

  return (
    <div className="min-h-[100svh] bg-[#0a0a0a]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-30 bg-[#0a0a0a]/90 backdrop-blur-xl border-b border-white/10">
        <div className="mx-auto max-w-[1200px] px-5 md:px-8 h-[68px] flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={IMAGES.logo} alt="Rayce Parts" className="h-5 w-auto" />
            <span className="font-mono-rp text-[11px] tracking-[0.25em] uppercase text-white/40 hidden sm:inline">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={sendTestSms}
              disabled={testingSms}
              className="btn-ghost !py-2.5 !px-4 text-xs disabled:opacity-60"
              data-testid="admin-test-sms-btn"
              title="Send a test OTP code to the admin phone"
            >
              {testingSms ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Smartphone size={14} />
              )}
              Test SMS
            </button>
            <button
              onClick={logout}
              className="btn-ghost !py-2.5 !px-4 text-xs"
              data-testid="admin-logout-btn"
            >
              <LogOut size={14} /> Log out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1200px] px-5 md:px-8 py-10">
        <h1 className="font-display text-5xl md:text-6xl text-white mb-8">
          {TITLES[tab]}
        </h1>

        <div className="flex gap-2 mb-8">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`flex items-center gap-2 font-mono-rp text-xs tracking-[0.15em] uppercase px-4 py-2.5 border transition-colors ${
                  tab === t.id
                    ? "border-[#e31e24] text-white bg-[#e31e24]/15"
                    : "border-white/15 text-white/50 hover:text-white"
                }`}
              >
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "enquiries" && <AdminEnquiries onConvert={convertToOrder} />}
        {tab === "orders" && (
          <AdminOrders
            prefill={orderPrefill}
            onPrefillConsumed={() => setOrderPrefill(null)}
          />
        )}
        {tab === "gallery" && <AdminGallery />}
      </div>
    </div>
  );
}
